/**
 * @module Experience/one-time-payment
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */ // Core Libraries
/* tslint:disable:no-any */ // We need to be able to allow developers send any payload the want

import { Injectable, EventEmitter } from '@angular/core';
import { Wove } from 'aspect.js/';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { API_MAP } from '@core/api/proxies/common-api-endpoints';
// Services
import { FormCreationService } from '@support/shared/services/form-creation.service';
import { ApiGatewayService } from '@core/api/services/api-gateway-service';
import { StateStoreUtilsService } from '@support/state-object-hash-utilities/utils/state-store-utils.service';
import { ContentProviderService } from '@core/aem/services/content-provider.service';
import { UserService } from '@support/shared/services/user.service';
import { CommonApiService } from '@support/shared/services/common-api/common-api.service';
import { CommonUtilService } from '@support/shared/services/common-util.service';
import { ObjectService } from '@support/state-object-hash-utilities/services/object.service';
import { CartSupportService } from '@support/cart-support/services/cart-support.service';
// Constants
import {
  ONE_TIME_PAYMENT_MODULE_NAME,
  ONE_TIME_PAYMENT_CONSTANTS,
} from '@experience/one-time-payment/constants/one-time-payment.constants';
// Model
import { OneTimePaymentModel } from '@experience/one-time-payment/model/one-time-payment.model';
import { OneTimePaymentConfirmation } from '@experience/one-time-payment/model/one-time-payment-confirmation';
// Interfaces
import { ConfigData } from '@experience/repdash/interfaces/aem-content.interface';
import { ShoppingCart } from '@experience/shop/cart/models/cart.model';
import { Cart, CartResponse } from '@support/api/proxies/responses/api-cart-response.interface';
import { FIELD_VALIDATIONS_CONSTANTS } from '@support/shared/services/common-api/common-api.constants';
import { CustomerAccountBillingDetails } from '@support/api/proxies/responses/api-2006.interface';
import { TaxExemption } from '@support/api/proxies/requests/api-2606.interface';
import { SegmentationResponse } from '@experience/directto-account/interfaces/directto-account.interface';
import { OnlineSubmitOrder } from '@support/payment/online-payment/models/online-submit-order.model';
import { SubmitOrderResponse } from '@support/api/proxies/responses/api-2150.interface';
import { of as observableOf, Observable } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';
import { HttpResponse, HttpHeaders } from '@angular/common/http';
import { PauseAndResumeResponse } from '@support/api/proxies/responses/api-2831.interface';

@Wove({ moduleName: ONE_TIME_PAYMENT_MODULE_NAME })
@Injectable()
export class OneTimePaymentService {
  public isAuthenticated: boolean;
  /**
   * variable to emit an event
   */
  public isTaxExemption: EventEmitter<Cart>;
  /**
   * variable to store error message
   */
  public errorMessage: string;
  /**
   * variable to track if edit payment cta is clicked
   */
  public isEditPaymentClicked: boolean;

  constructor(
    private userService: UserService,
    private contentService: ContentProviderService,
    private formBuilder: FormBuilder,
    private formCreationService: FormCreationService,
    private stateStoreUtilsService: StateStoreUtilsService,
    private apiGateway: ApiGatewayService,
    private objectService: ObjectService,
    private commonUtilService: CommonUtilService,
    private commonApiService: CommonApiService,
    private cartSupportService: CartSupportService
  ) {
    this.isTaxExemption = new EventEmitter();
    this.isEditPaymentClicked = false;
  }

  /**
   * Builds the form
   */
  public buildForm(): FormGroup {
    const oneTimePaymentForm = this.formBuilder.group({
      amountForm: this.buildAmountForm(),
    });
    return oneTimePaymentForm;
  }
  /**
   * Builds the payment form for one time payment flow
   */
  public buildPaymentForm(): FormGroup {
    const paymentForm = this.formBuilder.group({
      payment: this.formBuilder.group({}),
      email: this.formCreationService.getFormControl(
        FIELD_VALIDATIONS_CONSTANTS.EMAIL_ADDRESS,
        '',
        false
      ),
    });
    return paymentForm;
  }
  /**
   * To set value if edit payment cta is clicked
   */
  public setEditPaymentClicked(clicked: boolean): void {
    this.isEditPaymentClicked = clicked;
  }
  /**
   * To get value if edit payment cta is clicked
   */
  public getEditPaymentClicked(): boolean {
    return this.isEditPaymentClicked;
  }
  /**
   * To get API_2006 data.
   */
  public getBillingBlockDetails(): Observable<CustomerAccountBillingDetails> {
    return this.userService.getCustomerAccountBillingSummary();
  }

  /**
   * Method to make a call API_493 to fetch getSubscriberSegmentation data.
   */
  public getSubscriberSegmentation(msisdn: string): Observable<string> {
    const msisdnField = 'msisdn';
    const params = {};
    params[msisdnField] = msisdn.replace(/[^a-zA-Z] /g, '');
    if(!this.stateStoreUtilsService.getSegmentationCode()){
      return this.commonApiService.getSubscriberSegmentation(params).pipe(
        map((apiData: SegmentationResponse) => {
          return apiData.segmentationCode;
        })
      );
    }
    else{
      return observableOf(this.stateStoreUtilsService.getSegmentationCode());
    }
  }

  /**
   * Gets the phone number from refill page entry.
   */
  public getUnauthPhoneNumber(): string {
    return this.stateStoreUtilsService.getOneTimePaymentNumber();
  }

  /**
   * Calls API 2603 for Cart
   */
  public postDirectToAccount(oneTimePayment: OneTimePaymentModel): Observable<Cart> {
    const billingType = this.stateStoreUtilsService.getCustomerBillingType();
    return this.buildDirectToAccountRequest(oneTimePayment).pipe(
      mergeMap((updatedOneTimePayment: OneTimePaymentModel) => {
        let  headers = new HttpHeaders();
        headers = headers.append(ONE_TIME_PAYMENT_CONSTANTS.BILLING_TYPE, billingType.toLocaleUpperCase());
        return this.apiGateway
          .postRequest(
            API_MAP.API_2603_paymentsDirectToAccount,
            updatedOneTimePayment.directToAccount,
            headers,
            null
          )
          .pipe(
            map((resp: HttpResponse<CartResponse>) => {
              const cart: Cart = resp.body.cart;
              this.stateStoreUtilsService.setCart(cart);
              this.stateStoreUtilsService.setCartId(cart.cartId);
              this.setCartDetails(oneTimePayment, cart);
              return cart;
            })
          );
      })
    );
  }
  /**
   * Get AEM authorization content data
   * @returns Object
   */
  public getAemContent(pageUrl?: string): Observable<Object> {
    return this.contentService.getContentData(pageUrl).pipe(
      map((aemData: Object) => {
        const contentData = {
          configurableData: null,
          authorableData: null,
        };
        contentData.configurableData = <ConfigData>this.contentService.getConfigurableData(aemData);
        contentData.authorableData = this.contentService.getAuthorableData(aemData);
        return contentData;
      })
    );
  }

  /** to set error message generated from API
   *
   */
  public setErrorMessage(error: string): void {
    this.errorMessage = error;
  }

  /** to return error message generated from API
   *
   */
  public getErrorMessage(): string {
    return this.errorMessage;
  }

  /** Clears OTP flow details from State Store
   *
   */
  public clearOTPDetails(): void {
    this.stateStoreUtilsService.clearOTPDetails();
  }

  /** Clears OTP cart details from State Store
   *
   */
  public clearOTPCartDetails(): void {
    const emptyCart: Cart = new ShoppingCart();
    this.stateStoreUtilsService.deleteCart();
    this.cartSupportService.announceCartUpdate(emptyCart);
  }
  /** Clears Billing Block Details from StateStore
   *
   */
  public clearBillingBlockDetails(): void {
    this.stateStoreUtilsService.clearBillingBlockDetails();
  }
  /**
   * Retrieve OTP Model information from State Store
   */
  public getOTPDetails(): OneTimePaymentModel {
    return this.stateStoreUtilsService.getOTPModelDetails();
  }
  /**
   * Updates OTP Model in state store
   * @param otpModel OTP model to update in state store
   */
  public setOTPDetails(otpModel: OneTimePaymentModel): void {
    this.stateStoreUtilsService.setOTPModelDetails(otpModel);
  }
  /**
   * get confirmation details
   * @returns {Observable<OneTimePaymentConfirmation>}
   */
  public getConfirmationDetails(newOrderId?: string): Observable<OneTimePaymentConfirmation> {
    const pathParams = {
      orderId: newOrderId ? newOrderId : this.stateStoreUtilsService.getNewCartId(),
    };
    const requestPayload = {
      accountId: this.stateStoreUtilsService.getAccountId() || '',
      sendOrderDetailsOnly: false,
    };
    return this.apiGateway
      .postRequest(API_MAP.API_2814_getOrderDetails, requestPayload, null, null, pathParams)
      .pipe(
        map((response: HttpResponse<Object>) => {
          const confirmationDetails = response.body;
          return new OneTimePaymentConfirmation(confirmationDetails, this.objectService);
        })
      );
  }
  /**
   * get Cart Id for Email Address notification
   */
  public getCartId(): string {
    return this.stateStoreUtilsService.getCartId();
  }

  public submitOrder(oneTimePayment?: OneTimePaymentModel): Observable<SubmitOrderResponse> {
    let submitOrder;
    if (oneTimePayment) {
      const fingerPrintId = this.cartSupportService.getDFPDetails();
      submitOrder = new OnlineSubmitOrder(
        oneTimePayment.directToAccount.cartId,
        ONE_TIME_PAYMENT_CONSTANTS.PURCHASEEQUIPMENT,
        oneTimePayment.total,
        oneTimePayment.paymentInformation,
        null,
        null,
        false,
        false,
        false,
        fingerPrintId
      );
    }
    return this.cartSupportService.commonSubmitOrder(submitOrder);
  }
  /**
   * method used for updating tax by exempting the tax applied
   */
  public taxExemption(taxExemptionFormGroup: FormGroup): void {
    const cartData = this.stateStoreUtilsService.getCart();
    const requestPayload: TaxExemption = {
      cartId: cartData.cartId,
      cartItemId: cartData.cartItemId,
      tax: {
        taxExemption: true,
        id: taxExemptionFormGroup.get('taxExemptionText').value,
      },
    };
    this.apiGateway
      .postRequest(API_MAP.API_2606_taxExemption, requestPayload, null, null)
      .subscribe((resp: HttpResponse<Cart>) => {
        const cart: Cart = resp.body;
        this.stateStoreUtilsService.setCart(cart);
        this.stateStoreUtilsService.setCartId(cart.cartId);
        this.isTaxExemption.emit(cart);
      });
  }

  /**
   * Method to send the pause/resume request for associated recording
   * @param {boolean} pause boolean that determines pause/resume
   * @return void
   */
  public postPauseAndResume(pause: boolean): void {
    this.commonApiService
      .postPauseAndResume(pause)
      .subscribe((callData: PauseAndResumeResponse) => {
        // Response not considered
      });
  }

  /**
   * Method to check if store is open
   * @returns boolean
   */
  public isStoreOpen(): boolean {
    return this.cartSupportService.isStoreOpen();
  }

  /**
   * Builds amount section of form group
   */
  private buildAmountForm(): FormGroup {
    return this.formBuilder.group({
      radioAmount: [''],
      otherAmount: [''],
      totalAmount: ['', Validators.required],
    });
  }

  /**
   * set cart details in one time payment
   * @param {OneTimePaymentModel} oneTimePayment
   * @param {Cart} cart
   */
  public setCartDetails(oneTimePayment: OneTimePaymentModel, cart: Cart): void {
    oneTimePayment.directToAccount.cartId = this.objectService.get(cart, 'cartId');
    oneTimePayment.directToAccount.cartItemId = this.objectService.get(cart, 'cartItemId');
    oneTimePayment.directToAccount.chargeCode = this.objectService.get(cart, 'chargeCode');
    oneTimePayment.directToAccount.productOfferingId = this.objectService.get(
      cart,
      'productOfferingId'
    );
    oneTimePayment.directToAccount.offeringVariant = {
      sku: this.objectService.get(cart, 'sku'),
    };
    oneTimePayment.tax = this.objectService.get(cart, 'pricing.tax.totalTax.amount');
    oneTimePayment.total = this.objectService.get(
      cart,
      'pricing.total.payNowPrice.salePrice.amount'
    );
  }

  /**
   * used to build direct to account request
   * @param {OneTimePaymentModel} oneTimePayment
   * @return {Observable<OneTimePaymentModel>}
   */
  private buildDirectToAccountRequest(
    oneTimePayment: OneTimePaymentModel
  ): Observable<OneTimePaymentModel> {
    // if (this.commonUtilService.isRetailChannel() && this.stateStoreUtilsService.getStoreData()) {
    //   const storeAddressObj = this.stateStoreUtilsService.getStoreData().address;
    //   storeAddressObj.geoCodeId = this.objectService.get(storeAddressObj, 'geoCodeID.@referenceLayer');
    //   oneTimePayment.directToAccount.setStoreDetails(storeAddressObj);
    // }
    if (oneTimePayment.isAuthenticated) {
      return this.getBillingBlockDetails().pipe(
        map((customerAccount: CustomerAccountBillingDetails) => {
          const customerId = this.stateStoreUtilsService.getCustomerId();
          // if (!this.commonUtilService.isWebChannel()) {
          //   customerId = this.stateStoreUtilsService.getBscsCustomerID();
          // }
          oneTimePayment.directToAccount.setCustomerData(
            oneTimePayment.isAuthenticated,
            oneTimePayment.phoneNumber,
            this.objectService.get(customerAccount, 'billingAddress.zip'),
            customerAccount.accountNumber,
            customerId
          );
          return oneTimePayment;
        })
      );
    } else {
      oneTimePayment.directToAccount.setCustomerData(
        oneTimePayment.isAuthenticated,
        oneTimePayment.phoneNumber
      );
      return observableOf(oneTimePayment);
    }
  }
}
